import React, { useRef, useState } from 'react';

export type UberDoordashPlatform = 'Uber Eats' | 'DoorDash';

interface UberDoordashOrderModalProps {
  onConfirm: (platform: UberDoordashPlatform, customerName: string) => void;
  onCancel: () => void;
}

const UberDoordashOrderModal: React.FC<UberDoordashOrderModalProps> = ({
  onConfirm,
  onCancel,
}) => {
  const [platform, setPlatform] = useState<UberDoordashPlatform | null>(null);
  const [customerName, setCustomerName] = useState('');
  const customerNameInputRef = useRef<HTMLInputElement>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!platform) return;
    // Read from DOM so soft keyboard "Done" doesn't lose the value (state can be one keystroke behind)
    const value = customerNameInputRef.current?.value?.trim() ?? customerName.trim();
    onConfirm(platform, value || 'Customer');
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 z-[60] flex justify-center items-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="bg-gradient-to-r from-pink-500 to-pink-600 text-white px-6 py-3">
          <h2 className="text-xl font-bold">Uber / DoorDash Order</h2>
          <p className="text-pink-100 text-sm">Select platform and customer name first, then add menu items. Name is for display only (not saved to customer database).</p>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Platform</label>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setPlatform('Uber Eats')}
                className={`flex-1 py-3 px-4 rounded-lg border-2 font-medium transition-colors ${
                  platform === 'Uber Eats'
                    ? 'border-pink-500 bg-pink-50 text-pink-700'
                    : 'border-gray-300 bg-white text-gray-700 hover:border-pink-300'
                }`}
              >
                Uber Eats
              </button>
              <button
                type="button"
                onClick={() => setPlatform('DoorDash')}
                className={`flex-1 py-3 px-4 rounded-lg border-2 font-medium transition-colors ${
                  platform === 'DoorDash'
                    ? 'border-pink-500 bg-pink-50 text-pink-700'
                    : 'border-gray-300 bg-white text-gray-700 hover:border-pink-300'
                }`}
              >
                DoorDash
              </button>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Customer name (for display only)</label>
            <input
              ref={customerNameInputRef}
              type="text"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              placeholder="e.g. John or Order #1234"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
            />
            <p className="text-xs text-gray-500 mt-1">This is not added to your customer database.</p>
          </div>
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 py-2 px-4 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!platform}
              className="flex-1 py-2 px-4 bg-pink-500 text-white rounded-lg font-medium hover:bg-pink-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Continue to menu
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UberDoordashOrderModal;
